import type { TutorialLocation } from "schemas/generated/activation";
import type { TutorialStep } from "../constants/activationContent";

import * as React from "react";

import { TUTORIAL_STEPS } from "@v2/features/activation/constants/activationContent";
import { useLocalStorageState } from "@v2/hooks/useLocalStorage";
import { activationRouter, activationUtils } from "@v2/trpc/routes/activation";

function getTutorialSteps({
  tutorialLocation,
}: {
  tutorialLocation: TutorialLocation;
}): TutorialStep[] {
  return TUTORIAL_STEPS.filter(
    (s) => s.tutorialLocation === tutorialLocation,
  ).sort((a, b) => a.order - b.order);
}

const STORAGE_KEY_PREFIX = "replo:tutorial:step:";

interface UseTutorialProgressOptions {
  /** Whether to automatically start fetching on mount */
  enabled?: boolean;
  /** Filter steps by tutorial location */
  tutorialLocation: TutorialLocation;
}

interface UseTutorialProgressReturn {
  isLoading: boolean;
  shouldShowTutorial: boolean;
  currentStep: TutorialStep | null;
  /** 0-indexed */
  currentStepIndex: number;
  totalSteps: number;
  advanceToNextStep: () => void;
  skipTutorial: () => void;
  isUpdating: boolean;
}

function isValidStepIndex(value: unknown): value is number {
  return typeof value === "number" && !Number.isNaN(value) && value >= 0;
}

export function useTutorialProgress({
  enabled = true,
  tutorialLocation,
}: UseTutorialProgressOptions): UseTutorialProgressReturn {
  const tutorialSteps = React.useMemo(
    () => getTutorialSteps({ tutorialLocation }),
    [tutorialLocation],
  );

  const storageKey = `${STORAGE_KEY_PREFIX}${tutorialLocation}`;
  const [currentStepIndex, setCurrentStepIndex] = useLocalStorageState(
    storageKey,
    0,
    { validate: isValidStepIndex },
  );

  const { data: tutorialStatus, isLoading } =
    activationRouter.getTutorialStatuses.useQuery(undefined, {
      enabled,
    });

  const { mutate: completeTutorial, isPending: isUpdating } =
    activationRouter.completeTutorial.useMutation({
      onSuccess: () => {
        setCurrentStepIndex(null);
        void activationUtils.getTutorialStatuses.invalidate();
      },
    });

  // Check if this tutorial location is already completed
  const isCompleted = React.useMemo(() => {
    if (!tutorialStatus) {
      return false;
    }
    return tutorialLocation === "homepage"
      ? tutorialStatus.hasCompletedHomepageTutorial
      : tutorialStatus.hasCompletedEditorTutorial;
  }, [tutorialStatus, tutorialLocation]);

  const currentStep =
    currentStepIndex !== null && currentStepIndex < tutorialSteps.length
      ? tutorialSteps[currentStepIndex] ?? null
      : null;

  const shouldShowTutorial = !isLoading && !isCompleted;

  const advanceToNextStep = React.useCallback(() => {
    const isLastStep = currentStepIndex === tutorialSteps.length - 1;

    if (isLastStep) {
      // Mark tutorial as complete
      completeTutorial({ tutorialLocation });
    } else {
      // Move to next step locally
      setCurrentStepIndex((prev) => (prev ?? 0) + 1);
    }
  }, [
    currentStepIndex,
    tutorialSteps.length,
    completeTutorial,
    tutorialLocation,
    setCurrentStepIndex,
  ]);

  const skipTutorial = React.useCallback(() => {
    completeTutorial({ tutorialLocation });
  }, [completeTutorial, tutorialLocation]);

  return {
    isLoading,
    shouldShowTutorial,
    currentStep,
    currentStepIndex,
    totalSteps: tutorialSteps.length,
    advanceToNextStep,
    skipTutorial,
    isUpdating,
  };
}
